import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { useI18n } from "../i18n";

export default function Digest() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [allCaps, setAllCaps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title =
      lang === "es"
        ? "Corridor Digest — Pulso semanal de fabricantes IMMEX (Chihuahua ↔ El Paso)"
        : "Corridor Digest — Weekly pulse of IMMEX manufacturing (Chihuahua ↔ El Paso)";
    const meta =
      document.querySelector('meta[name="description"]') ||
      Object.assign(document.createElement("meta"), { name: "description" });
    meta.setAttribute(
      "content",
      lang === "es"
        ? "El pulso quincenal del corredor Chihuahua ↔ El Paso: fabricantes IMMEX destacados, capacidades demandadas, certificaciones activas y nuevas plantas listadas."
        : "The bi-weekly pulse of the Chihuahua ↔ El Paso corridor: featured IMMEX manufacturers, in-demand capabilities, active certifications and newly listed plants."
    );
    if (!meta.parentNode) document.head.appendChild(meta);
  }, [lang]);

  useEffect(() => {
    (async () => {
      try {
        const [digestRes, capsRes] = await Promise.all([
          api.get("/public/digest"),
          api.get("/public/capabilities"),
        ]);
        setData(digestRes.data);
        setAllCaps(capsRes.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="container-cd py-20 overline" data-testid="digest-loading">
        {t("common.loading")}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="container-cd py-20 overline" data-testid="digest-error">
        {t("common.error")}
      </div>
    );
  }

  return (
    <div data-testid="digest-page">
      {/* HERO */}
      <section className="container-cd pt-16 pb-12" style={{ borderBottom: "1px solid var(--line)" }}>
        <div className="overline">
          CORRIDOR DIGEST · {lang === "es" ? "EDICIÓN PRELIMINAR" : "PREVIEW ISSUE"}
        </div>
        <h1 className="font-display text-5xl md:text-7xl mt-4 leading-[0.9] max-w-5xl">
          {lang === "es"
            ? "El pulso quincenal del corredor Chihuahua ↔ El Paso."
            : "The bi-weekly pulse of the Chihuahua ↔ El Paso corridor."}
        </h1>
        <p className="mt-6 max-w-3xl text-base md:text-lg" style={{ color: "var(--ink-2)" }}>
          {lang === "es"
            ? "Fabricantes IMMEX destacados, capacidades de mayor demanda, certificaciones listadas y nuevas plantas listadas. Lectura única, bilingüe, hecha para compradores estadounidenses."
            : "Featured IMMEX manufacturers, in-demand capabilities, listed certifications and newly listed plants. One read, bilingual, built for US buyers."}
        </p>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Stat
            v={data.totals.manufacturers}
            k={lang === "es" ? "Plantas listadas" : "Plants listed"}
          />
          <Stat
            v={data.totals.verified}
            k={lang === "es" ? "Verificadas" : "Verified"}
          />
          <Stat
            v={data.top_capabilities.length}
            k={lang === "es" ? "Categorías de capacidad" : "Capability categories"}
          />
          <Stat
            v={data.top_cities.length}
            k={lang === "es" ? "Ciudades del corredor" : "Corridor cities"}
          />
        </div>
      </section>

      {/* TOP CAPABILITIES */}
      <section className="container-cd py-16">
        <div className="overline">
          {lang === "es" ? "CAPACIDADES MÁS DEMANDADAS" : "MOST IN-DEMAND CAPABILITIES"}
        </div>
        <h2 className="font-display text-3xl md:text-5xl mt-3 leading-tight">
          {lang === "es"
            ? "Lo que el corredor fabrica esta quincena."
            : "What the corridor is building this fortnight."}
        </h2>
        <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--ink-2)" }}>
          {lang === "es"
            ? "Toca una capacidad para ver todas las plantas que la ofrecen."
            : "Tap a capability to see every plant offering it."}
        </p>
        <div className="mt-8 flex flex-wrap gap-3" data-testid="digest-capabilities">
          {data.top_capabilities.map((c) => (
            <Link
              key={c.slug}
              to={`/c/${c.slug}`}
              className="cd-chip cd-chip-interactive font-mono text-sm"
              data-testid={`digest-cap-${c.slug}`}
            >
              {c.name} <span style={{ color: "var(--orange)" }}>· {c.count}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* FULL CAPABILITY INDEX — every capability becomes a crawlable landing entry */}
      {allCaps.length > 0 && (
        <section className="container-cd pb-16" data-testid="digest-all-capabilities-section">
          <div className="overline">
            {lang === "es" ? "ÍNDICE COMPLETO DE CAPACIDADES" : "FULL CAPABILITY INDEX"}
          </div>
          <h3 className="font-display text-2xl md:text-3xl mt-3 max-w-3xl">
            {lang === "es"
              ? `Las ${allCaps.length} capacidades del corredor, una por una.`
              : `All ${allCaps.length} corridor capabilities, one by one.`}
          </h3>
          <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--ink-2)" }}>
            {lang === "es"
              ? "Cada capacidad abre su propia página con las plantas que la ofrecen."
              : "Each capability opens its own page with the plants that offer it."}
          </p>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="digest-all-capabilities">
            {allCaps.map((c) => (
              <Link
                key={c.slug}
                to={`/c/${c.slug}`}
                className="cd-chip cd-chip-interactive font-mono text-xs"
                data-testid={`digest-all-cap-${c.slug}`}
              >
                {c.name} · {c.count}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FEATURED */}
      {data.featured.length > 0 && (
        <section className="container-cd py-16" style={{ borderTop: "1px solid var(--line)" }}>
          <div className="overline">
            {lang === "es" ? "DESTACADOS" : "FEATURED"}
          </div>
          <h2 className="font-display text-3xl md:text-5xl mt-3 leading-tight">
            {lang === "es"
              ? "Fabricantes que vale la pena conocer."
              : "Manufacturers worth knowing."}
          </h2>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="digest-featured">
            {data.featured.map((m) => (
              <ProfileCard key={m.profile_id} m={m} lang={lang} />
            ))}
          </div>
        </section>
      )}

      {/* RECENT */}
      {data.recent.length > 0 && (
        <section className="container-cd py-16" style={{ borderTop: "1px solid var(--line)" }}>
          <div className="overline">
            {lang === "es" ? "RECIÉN LISTADOS" : "RECENTLY LISTED"}
          </div>
          <h2 className="font-display text-3xl md:text-5xl mt-3 leading-tight">
            {lang === "es" ? "Nuevos en el corredor." : "New on the corridor."}
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-6" data-testid="digest-recent">
            {data.recent.slice(0, 4).map((m) => (
              <ProfileRow key={m.profile_id} m={m} lang={lang} />
            ))}
          </div>
        </section>
      )}

      {/* CERTS + CITIES split */}
      <section className="container-cd py-16 grid md:grid-cols-2 gap-12" style={{ borderTop: "1px solid var(--line)" }}>
        <div>
          <div className="overline">
            {lang === "es" ? "CERTIFICACIONES LISTADAS" : "LISTED CERTIFICATIONS"}
          </div>
          <h3 className="font-display text-2xl md:text-3xl mt-3">
            {lang === "es" ? "Certificaciones, contadas." : "Certifications, counted."}
          </h3>
          <div className="mt-6 space-y-2" data-testid="digest-certs">
            {data.top_certifications.map((c) => (
              <div
                key={c.name}
                className="flex items-center justify-between py-2"
                style={{ borderBottom: "1px dashed var(--line)" }}
              >
                <span className="font-heading text-sm">{c.name}</span>
                <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                  {c.count} {lang === "es" ? "planta(s)" : "plant(s)"}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="overline">
            {lang === "es" ? "CIUDADES DEL CORREDOR" : "CORRIDOR CITIES"}
          </div>
          <h3 className="font-display text-2xl md:text-3xl mt-3">
            {lang === "es" ? "Dónde se produce." : "Where it gets built."}
          </h3>
          <div className="mt-6 space-y-2" data-testid="digest-cities">
            {data.top_cities.map((c) => (
              <div
                key={c.name}
                className="flex items-center justify-between py-2"
                style={{ borderBottom: "1px dashed var(--line)" }}
              >
                <span className="font-heading text-sm">{c.name}</span>
                <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                  {c.count} {lang === "es" ? "planta(s)" : "plant(s)"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-16"
        style={{ background: "var(--ink)", color: "white", borderTop: "1px solid var(--line)" }}
      >
        <div className="container-cd grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline" style={{ color: "var(--orange)" }}>
              {lang === "es" ? "CORRIDOR DIGEST · MENSUAL" : "CORRIDOR DIGEST · MONTHLY"}
            </div>
            <h2 className="font-display text-3xl md:text-5xl mt-3 leading-tight">
              {lang === "es"
                ? "Recibe el digest en tu bandeja."
                : "Get the digest in your inbox."}
            </h2>
            <p className="mt-4 text-base" style={{ color: "rgba(255,255,255,0.7)", maxWidth: 520 }}>
              {lang === "es"
                ? "Una edición mensual con nuevos fabricantes, capacidades emergentes y RFQs activos del corredor. Sin spam. Cancelar en un clic."
                : "One monthly edition with new manufacturers, emerging capabilities and active corridor RFQs. No spam. Unsubscribe in one click."}
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <Link
              to="/register"
              className="btn btn-primary w-full justify-center"
              data-testid="digest-cta-register"
            >
              {lang === "es"
                ? "Lista tu planta gratis"
                : "List your plant free"}
            </Link>
            <Link
              to="/directory"
              className="btn btn-ghost w-full justify-center"
              style={{ color: "white", borderColor: "rgba(255,255,255,0.4)" }}
              data-testid="digest-cta-directory"
            >
              {lang === "es" ? "Explorar el directorio" : "Browse the directory"}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ v, k }) {
  return (
    <div className="cd-card p-6">
      <div className="font-display text-4xl md:text-5xl">{v}</div>
      <div className="overline mt-2" style={{ color: "var(--ink-2)" }}>{k}</div>
    </div>
  );
}

function ProfileCard({ m, lang }) {
  const tagline = lang === "es" ? m.tagline_es || m.tagline_en : m.tagline_en || m.tagline_es;
  return (
    <Link
      to={`/m/${m.slug}`}
      className="cd-card cd-card-interactive p-6 block"
      data-testid={`digest-featured-${m.slug}`}
    >
      <div className="flex items-center gap-3">
        <h3 className="font-heading font-semibold text-xl">{m.company_name}</h3>
        {m.verified ? <span className="cd-chip cd-chip-dark">✓</span> : null}
      </div>
      <div className="mt-1 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
        {m.city}{m.state ? `, ${m.state}` : ""}
      </div>
      {tagline ? (
        <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>{tagline}</p>
      ) : null}
      <div className="tag-list mt-4">
        {(m.capabilities || []).slice(0, 4).map((c, i) => (
          <span key={i} className="cd-chip">{c}</span>
        ))}
      </div>
    </Link>
  );
}

function ProfileRow({ m, lang }) {
  const tagline = lang === "es" ? m.tagline_es || m.tagline_en : m.tagline_en || m.tagline_es;
  return (
    <Link
      to={`/m/${m.slug}`}
      className="cd-card cd-card-interactive p-6 block"
      data-testid={`digest-recent-${m.slug}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-heading font-semibold text-lg">{m.company_name}</h3>
        <span className="cd-chip cd-chip-orange">
          {(m.subscription_tier || "free").toUpperCase()}
        </span>
      </div>
      <div className="mt-1 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
        {m.city}{m.state ? `, ${m.state}` : ""}
      </div>
      {tagline ? (
        <p className="mt-2 text-sm" style={{ color: "var(--ink-2)" }}>{tagline}</p>
      ) : null}
    </Link>
  );
}
